// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {RemovableTag, RemoveAction} from '@wandb/weave/components/Tag';
import {isValidEmail} from '@wandb/weave/core/util/string';
import React, {useEffect, useState} from 'react';

type UsersToInviteInputProps = {
  value: string[];
  onChange: (updateFn: (prevUsers: string[]) => string[]) => void;
  onValidationChange: (hasInvalidEmails: boolean) => void;
  maxUsers: number;
  placeholder: string;
  usernameInviteEnabled: boolean;
  failedInvites?: string[]; // todo(sammy): make this required
};

export const UsersToInviteInput: React.FC<UsersToInviteInputProps> = ({
  value: userTags,
  onChange: setUserTags,
  onValidationChange,
  maxUsers,
  placeholder,
  usernameInviteEnabled,
  failedInvites,
}) => {
  const [currentUserInput, setCurrentUserInput] = useState<string>('');
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);

  // Notify parent component when validation state changes
  useEffect(() => {
    onValidationChange?.(invalidEmails.length > 0);
  }, [invalidEmails, onValidationChange]);

  const removeUserTag = (user: string) => {
    setUserTags(prevTags => prevTags.filter(tag => tag !== user));
    setInvalidEmails(prevInvalid =>
      prevInvalid.filter(email => email !== user)
    );
  };

  const handleUserInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === 'Enter' ||
      e.key === ' ' ||
      e.key === ',' ||
      e.key === 'Tab'
    ) {
      e.preventDefault();
      addUserTags(currentUserInput);
    } else if (
      e.key === 'Backspace' &&
      !currentUserInput &&
      userTags.length > 0
    ) {
      // Remove last tag if backspace on empty input.
      removeUserTag(userTags[userTags.length - 1]);
    }
  };

  const addUserTags = (user: string) => {
    // Split on commas and spaces, then filter out empty strings and duplicates
    const splitTags = user
      .split(/[ ,]+/)
      .map(u => u.trim())
      .filter(u => u);

    if (splitTags.length === 0) {
      setCurrentUserInput('');
      return;
    }

    // Use functional update to avoid race conditions
    setUserTags(prevTags => {
      const newTags = splitTags.filter(tag => !prevTags.includes(tag));
      return [...prevTags, ...newTags];
    });

    const filterInvalidUsers = (user: string) => {
      if (usernameInviteEnabled) {
        return user.includes('@') && !isValidEmail(user);
      }

      return !isValidEmail(user);
    };

    // Check for invalid emails in the new tags
    const newInvalidEmails = splitTags.filter(filterInvalidUsers);
    if (newInvalidEmails.length > 0) {
      setInvalidEmails(prevInvalid => [...prevInvalid, ...newInvalidEmails]);
    }

    setCurrentUserInput('');
  };

  const clearAllUserTags = () => {
    setUserTags(() => []);
    setInvalidEmails(() => []);
    setCurrentUserInput('');
  };

  return (
    <>
      <div
        className={`
          w-full min-h-[32px] px-12 py-6 rounded-md
          border-0 outline outline-1 outline-moon-250
          flex cursor-text
          hover:outline-2 hover:outline-teal-350
          focus-within:outline-2 focus-within:outline-teal-450 focus-within:hover:outline-teal-450
        `}>
        <div className="flex flex-wrap gap-2 items-center flex-1 min-w-0">
          {userTags.map(user => {
            const isInvalidEmail = invalidEmails.includes(user);

            return (
              <RemovableTag
                key={user}
                color={
                  isInvalidEmail || failedInvites?.includes(user)
                    ? 'red'
                    : 'moon'
                }
                label={user}
                removeAction={
                  <RemoveAction
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      removeUserTag(user);
                    }}
                  />
                }
                Wrapper={null}
              />
            );
          })}
          <input
            id="bulk-email-input"
            type="text"
            value={currentUserInput}
            onChange={e => setCurrentUserInput(e.target.value)}
            onKeyDown={handleUserInputKeyDown}
            onBlur={() => {
              if (currentUserInput.trim()) {
                addUserTags(currentUserInput);
              }
            }}
            onPaste={e => {
              e.preventDefault();
              const pastedText = e.clipboardData.getData('text');
              addUserTags(pastedText);
            }}
            placeholder={userTags.length === 0 ? placeholder : ''}
            className="flex-1 min-w-0 border-0 outline-none bg-transparent text-md placeholder:text-moon-500 py-1"
            disabled={userTags.length >= maxUsers}
          />
        </div>
        {userTags.length > 0 && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              clearAllUserTags();
            }}
            className="ml-2 flex items-center cursor-pointer opacity-60 hover:opacity-100 shrink-0 self-center"
            title="Clear all">
            <Icon className="h-14 w-14" name="close" />
          </button>
        )}
      </div>
      {/* Reserve space for error message to prevent layout shift */}
      <div className="h-5 mt-1">
        {(invalidEmails.length > 0 && (
          <div className="text-red-500 text-sm">
            Invalid email format - Remove invalid entries or fix them to
            continue
          </div>
        )) ??
          (userTags.length > 0 && (
            <div className="text-moon-500 text-sm">Add up to 10 users.</div>
          ))}
      </div>
    </>
  );
};
